import { reportSessionEvent } from './session-diagnostics';

// TKT-000801 (Beckham) / TKT-000853 (Logan). After a frontend release, clicking a tab the
// browser has not opened yet does nothing at all - the click is simply lost - while screens
// already on the page keep working. Ctrl+R clears it, which is why it reads as "frozen".
//
// Every route component is lazy-loaded and the bundler names each chunk by a content hash,
// so a release renames nearly all of them. A browser holding the index.html it loaded before
// the release still asks for the OLD names; nginx 404s those now (it used to answer with
// index.html), the dynamic import rejects, and because nothing was registered with
// router.onError vue-router aborts the navigation in silence. The silence is the bug: the
// app is not frozen, it has given up on one navigation without saying so.
//
// What this module does about it:
//   - A ROUTE whose chunk fails is reloaded from the server AT THE TARGET URL. Safe for the
//     user's work: they were already leaving the screen they were on (a tab click discards
//     it anyway, and the app registers no beforeRouteLeave/beforeunload guard), and what the
//     session needs - auth.jwt/auth.user/auth.exp and workstationID - lives in localStorage,
//     which a reload does not touch.
//   - An IN-PAGE lazy load (a dashboard widget, a dialog) is NEVER reloaded. The user is
//     standing on that screen with search criteria or a half-keyed form in front of them, and
//     reloading would throw that away to fix a widget. It retries once, then says so.
//   - At most one automatic reload per target path per RELOAD_WINDOW_MS, so a chunk that is
//     permanently missing - mid-deployment, say - costs one reload and then a message rather
//     than an endless loop.
//
// Deliberately NOT here: a global 'vite:preloadError' listener that reloads. That event fires
// for in-page imports as well as route ones and carries nothing that distinguishes them, so a
// listener hung off it would reload exactly the case that must not. The import rejects either
// way, so the two entry points below already cover Vite's preload failures.
//
// This file is identical on both frontends (master/Vue 2 + webpack, main/Vue 3 + Vite) and
// imports nothing framework-specific on purpose - the router is passed in.

export const RELOAD_WINDOW_MS = 60 * 1000;

export const NAV_RELOAD_BLOCKED_MESSAGE = 'Could not open that screen because the app has been updated. Please press Ctrl+R to refresh.';

export const ASYNC_LOAD_FAILED_MESSAGE = "This part of the screen couldn't load - save your work and refresh.";

// Both bundlers are covered because both frontends are in scope and they word this
// differently - webpack (master) raises ChunkLoadError, Vite (main) rejects with a browser
// message whose wording differs per engine. Getting this list wrong fails safe: an
// unrecognised error is left alone and behaves exactly as it does today.
const CHUNK_ERROR_PATTERN = new RegExp([
  'ChunkLoadError', //                              webpack: err.name, and usually in the text
  'Loading chunk \\S+ failed', //                   webpack: JS chunk
  'Loading CSS chunk \\S+ failed', //               webpack: mini-css-extract
  'Failed to fetch dynamically imported module', // Vite: Chrome/Edge
  'Importing a module script failed', //            Vite: Safari
  'error loading dynamically imported module', //   Vite: Firefox
  'Unable to preload CSS', //                       Vite: __vitePreload on a stylesheet
].join('|'), 'i');

export const isChunkLoadError = (err) => {
  if (!err) return false;
  try {
    if (err.name === 'ChunkLoadError' || err.code === 'CSS_CHUNK_LOAD_FAILED') return true;
    // vue-router 3 re-wraps a non-Error rejection as "Failed to resolve async component
    // default: <reason>", so the original wording survives in the message either way.
    const text = typeof err === 'string' ? err : `${err.message || ''} ${err.name || ''}`;
    return CHUNK_ERROR_PATTERN.test(text);
  } catch (e) {
    return false;
  }
};

// Exported so the specs - and anyone reproducing this at a county - can see and clear the
// budget from the console.
export const reloadKeyFor = (path) => `tmc.chunkReload.${path}`;

// sessionStorage rather than localStorage on purpose: the budget should die with the tab, and
// must not be shared with other tabs, which may be running a different bundle entirely.
const claimReload = (path) => {
  try {
    const key = reloadKeyFor(path);
    const now = Date.now();
    const last = Number(window.sessionStorage.getItem(key));
    if (last > 0 && now - last < RELOAD_WINDOW_MS) return false;
    window.sessionStorage.setItem(key, String(now));
    return true;
  } catch (e) {
    // sessionStorage can throw outright (blocked cookies, some private modes). Without it
    // there is no way to remember the attempt across the reload, so there is no way to stop
    // at one - and a reload loop is far worse than a tab that will not open. Don't start.
    return false;
  }
};

// How the user gets told. Set once at startup rather than importing the store here, because
// the store already reaches this module through store/dashboard -> widgetRegistry and a cycle
// back would be a boot-order trap for whoever touches it next.
let notifyHandler = null;

export const setAsyncLoadFailureHandler = (fn) => {
  notifyHandler = fn;
};

const notify = (message) => {
  try {
    if (notifyHandler) notifyHandler(message);
  } catch (e) {
    // telling the user about a failure must not become one
  }
};

const errText = (err) => String((err && err.message) || err || 'unknown').slice(0, 120);

/**
 * Route navigation failures only. On a failed route-component chunk, reload the page at the
 * route the user asked for, once per path per RELOAD_WINDOW_MS.
 *
 * Call once, after the router is created.
 */
export const installNavigationChunkRecovery = (router) => {
  // vue-router 3 hands onError nothing but the error; vue-router 4 also hands it `to`.
  // Recording the target as navigation starts covers both - and it is the only way to know
  // WHICH page to reload, since window.location is still the old URL when the import rejects.
  let pending = null;
  router.beforeEach((to, from, next) => {
    pending = to;
    next(); // required: vue-router 3 hangs the navigation without it, and v4 accepts it
  });

  router.onError((err, to) => {
    // Anything else - a guard throwing, a redirect, a duplicated navigation - is left exactly
    // as it behaves today. Only a missing chunk is ours.
    if (!isChunkLoadError(err)) return;

    const target = to || pending;
    if (!target) return;

    let href = target.fullPath;
    try {
      // resolve() rather than fullPath alone so a non-root router base is preserved
      href = router.resolve(target.fullPath).href;
    } catch (e) {
      // keep fullPath
    }

    // Keyed on path, not fullPath: one stale chunk backs one screen, and keying on the query
    // string would let a screen with changing criteria reload over and over.
    const allowed = claimReload(target.path || href);

    reportSessionEvent('chunk-error', {
      message: `route chunk failed, ${allowed ? 'reloading' : 'reload already used'}: ${errText(err)}`,
      source: String(href).slice(0, 256),
    });

    if (allowed) window.location.assign(href);
    else notify(NAV_RELOAD_BLOCKED_MESSAGE);
  });
};

/**
 * In-page lazy loads (dashboard widgets, async dialogs) - the ones that must NOT reload.
 * Wraps an `() => import('...')` loader so it retries once and then reports.
 *
 * Usage: `component: retryDynamicImport(() => import('./Foo.vue'))`
 */
export const retryDynamicImport = (loader) => () => loader().catch((firstError) => {
  if (!isChunkLoadError(firstError)) throw firstError;
  // One retry. A chunk the server genuinely no longer has 404s again and we fall through to
  // the message; a transient failure - a dropped connection, a proxy hiccup mid-release - is
  // what this actually rescues. It is a real second request: both bundlers drop a failed
  // chunk from their cache rather than handing back the rejected promise.
  return loader().catch((secondError) => {
    notify(ASYNC_LOAD_FAILED_MESSAGE);
    reportSessionEvent('chunk-error', {
      message: `in-page chunk failed after retry: ${errText(secondError)}`,
    });
    // rethrown so the caller still sees a failed component rather than an empty success
    throw secondError;
  });
});

export default {
  installNavigationChunkRecovery,
  retryDynamicImport,
  setAsyncLoadFailureHandler,
  isChunkLoadError,
};
